import { jsxs, jsx } from "react/jsx-runtime";
import styled, { css } from "styled-components";
import { token } from "./token.js";
import { pointerEventsClassname, Overlay } from "./Overlay.js";
import { GAP, INDICATORS_VALUES } from "./constants2.js";
import { Indicator } from "./Indicator.js";
import { Status } from "./Status.js";
import { useId } from "./useId.js";
function Thumbnail({
  size = "m",
  shape = "square",
  variant = "clean",
  icon,
  imageUrl,
  backgroundColor: backgroundColorProp,
  iconColor: iconColorProp,
  children,
  ...domProps
}) {
  const isContentVariant = variant === "content";
  const isCleanVariant = variant === "clean";
  const backgroundColor = (() => {
    if (backgroundColorProp) {
      return token[backgroundColorProp] || backgroundColorProp;
    } else if (isCleanVariant) {
      return token.gray50a;
    } else if (!backgroundColorProp && !imageUrl) {
      return token.white;
    }
    return "transparent";
  })();
  const iconColor = isCleanVariant ? token[iconColorProp] || iconColorProp || token.gray600 : void 0;
  const borderRadius = (() => {
    if (shape === "circle") {
      return "50%";
    }
    if (variant === "preview") {
      return "0";
    }
    return "5px";
  })();
  const maskId = useId("thumbnail-mask");
  return /* @__PURE__ */ jsxs(ThumbnailContainer, {
    $size: size,
    $shape: shape,
    className: pointerEventsClassname,
    style: {
      "--ds-thumbnail-border-radius": borderRadius
    },
    ...domProps,
    children: [/* @__PURE__ */ jsxs(Svg, {
      role: "none",
      children: [/* @__PURE__ */ jsxs("mask", {
        id: maskId,
        children: [/* @__PURE__ */ jsx("rect", {
          fill: "white",
          x: "0",
          y: "0",
          width: "100%",
          height: "100%",
          rx: borderRadius
        }), /* @__PURE__ */ jsx(Cutout, {})]
      }), /* @__PURE__ */ jsxs("g", {
        mask: `url(#${maskId})`,
        children: [isContentVariant && /* @__PURE__ */ jsx(Checkers, {}), /* @__PURE__ */ jsx("rect", {
          fill: backgroundColor,
          x: "0",
          y: "0",
          width: "100%",
          height: "100%",
          rx: borderRadius,
          "data-testid": "ds-thumbnail-fill"
        }), imageUrl && /* @__PURE__ */ jsx("image", {
          x: "0",
          y: "0",
          width: "100%",
          height: "100%",
          preserveAspectRatio: `xMidYMid ${variant === "preview" ? `meet` : `slice`}`,
          xlinkHref: imageUrl,
          "data-testid": "ds-thumbnail-image"
        }), isContentVariant && /* @__PURE__ */ jsx("rect", {
          fill: "none",
          x: "0",
          y: "0",
          width: "100%",
          height: "100%",
          stroke: "rgba(0,0,0,0.1)",
          strokeWidth: "2",
          rx: borderRadius,
          "data-testid": "ds-thumbnail-inner-shadow"
        })]
      })]
    }), /* @__PURE__ */ jsxs(Content, {
      $iconColor: iconColor,
      $size: size,
      "data-testid": "ds-thumbnail-content",
      children: [icon, isContentVariant && !backgroundColorProp && !imageUrl && /* @__PURE__ */ jsx(NoImage, {
        "data-testid": "ds-thumbnail-no-image-line"
      })]
    }), children]
  });
}
Thumbnail.Overlay = Overlay;
Thumbnail.pointerEventsClassname = pointerEventsClassname;
Thumbnail.Indicator = Indicator;
Thumbnail.Status = Status;
const sizes = {
  xs: "24px",
  s: "32px",
  m: "40px",
  l: "64px"
};
const ThumbnailContainer = styled.div.withConfig({
  displayName: "ThumbnailContainer",
  componentId: "sc-n2cpv6-0"
})(["position:relative;flex:none;width:", ";height:", ";&:has([data-thumbnail-status]){", "}&:has([data-thumbnail-indicator]){", "}"], (props) => sizes[props.$size], (props) => sizes[props.$size], getIndicatorPosition("status"), getIndicatorPosition("indicator"));
function getIndicatorPosition(indicatorType) {
  return (props) => {
    const {
      radius,
      offset
    } = INDICATORS_VALUES[indicatorType][props.$shape][props.$size];
    return css(["--ds-thumbnail-indicator-offset-x:", "px;--ds-thumbnail-indicator-offset-y:", "px;--ds-thumbnail-indicator-radius:", "px;--ds-thumbnail-overlay-indicator-cutout:-webkit-radial-gradient( calc( 100% - ( var(--ds-thumbnail-indicator-radius) - var( --ds-thumbnail-indicator-offset-x ) ) ) calc( 100% - ( var(--ds-thumbnail-indicator-radius) - var( --ds-thumbnail-indicator-offset-y ) ) ),circle,transparent calc(var(--ds-thumbnail-indicator-radius) + ", "px),black 0 );--ds-thumbnail-indicator-color:black;"], offset[0], offset[1], radius, GAP);
  };
}
const Svg = styled.svg.withConfig({
  displayName: "Svg",
  componentId: "sc-n2cpv6-1"
})(["position:absolute;top:0;left:0;width:100%;height:100%;"]);
const Cutout = styled.circle.withConfig({
  displayName: "Cutout",
  componentId: "sc-n2cpv6-2"
})(["fill:var(--ds-thumbnail-indicator-color,white);cx:calc(100% - var(--ds-thumbnail-indicator-radius) + var(--ds-thumbnail-indicator-offset-x));cy:calc(100% - var(--ds-thumbnail-indicator-radius) + var(--ds-thumbnail-indicator-offset-y));r:calc(var(--ds-thumbnail-indicator-radius) + ", "px);"], GAP);
const fontSizes = {
  xs: token.fontSize100,
  s: token.fontSize200,
  m: token.fontSize500,
  l: token.fontSize500
};
const Content = styled.div.withConfig({
  displayName: "Content",
  componentId: "sc-n2cpv6-3"
})(["position:absolute;inset:0;display:grid;place-items:center;color:", ";font-weight:600;font-size:", ";line-height:1;"], (props) => props.$iconColor, (props) => fontSizes[props.$size]);
const NoImage = styled.div.withConfig({
  displayName: "NoImage",
  componentId: "sc-n2cpv6-4"
})(["width:80%;height:2px;border-radius:2px;background:", ";transform:rotate(-45deg);"], token.red500);
function Checkers() {
  return /* @__PURE__ */ jsxs("g", {
    children: [/* @__PURE__ */ jsx("rect", {
      height: "64",
      width: "64",
      x: "0",
      y: "0",
      fill: "#ffffff"
    }), /* @__PURE__ */ jsx("path", {
      fill: "#e9ecee",
      d: "M0 0h4v4H0zM0 8h4v4H0zM0 16h4v4H0zM0 24h4v4H0zM0 32h4v4H0zM0 40h4v4H0zM0 48h4v4H0zM0 56h4v4H0zM4 4h4v4H4zM4 12h4v4H4zM4 20h4v4H4zM4 28h4v4H4zM4 36h4v4H4zM4 44h4v4H4zM4 52h4v4H4zM4 60h4v4H4zM8 0h4v4H8zM8 8h4v4H8zM8 16h4v4H8zM8 24h4v4H8zM8 32h4v4H8zM8 40h4v4H8zM8 48h4v4H8zM8 56h4v4H8zM12 4h4v4h-4zM12 12h4v4h-4zM12 20h4v4h-4zM12 28h4v4h-4zM12 36h4v4h-4zM12 44h4v4h-4zM12 52h4v4h-4zM12 60h4v4h-4zM16 0h4v4h-4zM16 8h4v4h-4zM16 16h4v4h-4zM16 24h4v4h-4zM16 32h4v4h-4zM16 40h4v4h-4zM16 48h4v4h-4zM16 56h4v4h-4zM20 4h4v4h-4zM20 12h4v4h-4zM20 20h4v4h-4zM20 28h4v4h-4zM20 36h4v4h-4zM20 44h4v4h-4zM20 52h4v4h-4zM20 60h4v4h-4zM24 0h4v4h-4zM24 8h4v4h-4zM24 16h4v4h-4zM24 24h4v4h-4zM24 32h4v4h-4zM24 40h4v4h-4zM24 48h4v4h-4zM24 56h4v4h-4zM28 4h4v4h-4zM28 12h4v4h-4zM28 20h4v4h-4zM28 28h4v4h-4zM28 36h4v4h-4zM28 44h4v4h-4zM28 52h4v4h-4zM28 60h4v4h-4zM32 0h4v4h-4zM32 8h4v4h-4zM32 16h4v4h-4zM32 24h4v4h-4zM32 32h4v4h-4zM32 40h4v4h-4zM32 48h4v4h-4zM32 56h4v4h-4zM36 4h4v4h-4zM36 12h4v4h-4zM36 20h4v4h-4zM36 28h4v4h-4zM36 36h4v4h-4zM36 44h4v4h-4zM36 52h4v4h-4zM36 60h4v4h-4zM40 0h4v4h-4zM40 8h4v4h-4zM40 16h4v4h-4zM40 24h4v4h-4zM40 32h4v4h-4zM40 40h4v4h-4zM40 48h4v4h-4zM40 56h4v4h-4zM44 4h4v4h-4zM44 12h4v4h-4zM44 20h4v4h-4zM44 28h4v4h-4zM44 36h4v4h-4zM44 44h4v4h-4zM44 52h4v4h-4zM44 60h4v4h-4zM48 0h4v4h-4zM48 8h4v4h-4zM48 16h4v4h-4zM48 24h4v4h-4zM48 32h4v4h-4zM48 40h4v4h-4zM48 48h4v4h-4zM48 56h4v4h-4zM52 4h4v4h-4zM52 12h4v4h-4zM52 20h4v4h-4zM52 28h4v4h-4zM52 36h4v4h-4zM52 44h4v4h-4zM52 52h4v4h-4zM52 60h4v4h-4zM56 0h4v4h-4zM56 8h4v4h-4zM56 16h4v4h-4zM56 24h4v4h-4zM56 32h4v4h-4zM56 40h4v4h-4zM56 48h4v4h-4zM56 56h4v4h-4zM60 4h4v4h-4zM60 12h4v4h-4zM60 20h4v4h-4zM60 28h4v4h-4zM60 36h4v4h-4zM60 44h4v4h-4zM60 52h4v4h-4zM60 60h4v4h-4z"
    })]
  });
}
export {
  Thumbnail
};
//# sourceMappingURL=Thumbnail.js.map
